/**
 * Dock-panel layout persistence store (`punica.shell.PanelLayout.manager`).
 *
 * Resolves a merged view of three tiers — Project > Global > Profile default —
 * and exposes it through SYNCHRONOUS getters, because the tabbed containers'
 * `open()` and the views analysis decorator consult it as each tab/placement is
 * created (during extension load, which runs after module `start()`).
 *
 * Storage reuses `SettingsManager` under the `'system'` pseudo-extension (same
 * precedent as `theme.profile`): `settings.get('system', key)` already yields
 * Project ?? Global; this store layers the active profile's defaults beneath
 * that. Writes go to global scope by default (the user's cross-workspace
 * preference) or project scope with `{ workspace: true }`.
 *
 * Preferences are **per container (slot)**. The bottom panel (`'panel'`, the
 * default) keeps its legacy UNSCOPED keys for backward compatibility; other
 * containers (e.g. `'secondarySidebar'`, the right vertical strip) use
 * slot-scoped keys (`panel.tabDisplay.<slot>`, …). `view.slotOverrides` stays
 * flat — a view maps to exactly one slot.
 */

import type { shell } from 'punica';

type TabDisplayMode = shell.PanelLayout.TabDisplayMode;
type SetOptions = shell.PanelLayout.SetOptions;

const KEY_DISPLAY = 'panel.tabDisplay';
const KEY_HIDDEN = 'panel.hiddenTabs';
const KEY_ORDER = 'panel.tabOrder';
const KEY_SLOT_OVERRIDES = 'view.slotOverrides';

const DEFAULT_SLOT = 'panel';

function settingsManager(): any {
  return (globalThis as any).punica?.extensions?.settings?.manager ?? null;
}

/** Active ApplicationProfile's `shell` spec, or null when no profile is active. */
function profileShell(): any {
  try {
    const profile = (
      globalThis as any
    ).punica?.shell?.Profile?.manager?.registry?.getActive?.();
    return profile?.shell ?? null;
  } catch {
    return null;
  }
}

class PanelLayoutManager implements shell.PanelLayout.IPanelLayoutManager {
  #loaded = false;
  #settings: any = null;

  // Cached resolved user tier (Project ?? Global) keyed by the full settings
  // key. Populated lazily on first read and updated immediately on write, so
  // getters stay synchronous. Profile defaults are read live from the active
  // profile so a profile switch is picked up on reload.
  #cache: Map<string, unknown> = new Map();
  #userSlotOverrides: Record<string, string> = {};

  async load(): Promise<void> {
    const settings = settingsManager();
    this.#settings = settings;
    if (settings) {
      try {
        await settings.load();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[PanelLayout] settings.load() failed', err);
      }
      this.#userSlotOverrides =
        this.#readRecord(settings.get('system', KEY_SLOT_OVERRIDES)) ?? {};
    }
    this.#loaded = true;
  }

  // ── Getters (synchronous; Project/Global cache ?? Profile default ?? built-in) ──

  getDisplayMode(slot: string = DEFAULT_SLOT): TabDisplayMode {
    const user = this.#readMode(this.#userValue(KEY_DISPLAY, slot));
    if (user) return user;
    if (slot === DEFAULT_SLOT) {
      const prof = this.#readMode(profileShell()?.panel?.tabDisplay);
      if (prof) return prof;
    }
    // The right (vertical) strip defaults to icon-only (activity-bar style);
    // the bottom (horizontal) strip to icon+label.
    return slot === DEFAULT_SLOT ? 'both' : 'icon';
  }

  getHiddenTabs(slot: string = DEFAULT_SLOT): string[] {
    const user = this.#readStringArray(this.#userValue(KEY_HIDDEN, slot));
    if (user) return user;
    if (slot === DEFAULT_SLOT) {
      const prof = this.#readStringArray(profileShell()?.panel?.hiddenTabs);
      if (prof) return prof;
    }
    return [];
  }

  isTabHidden(id: string, slot: string = DEFAULT_SLOT): boolean {
    return this.getHiddenTabs(slot).includes(id);
  }

  getTabOrder(slot: string = DEFAULT_SLOT): string[] | undefined {
    const user = this.#readStringArray(this.#userValue(KEY_ORDER, slot));
    if (user) return user;
    if (slot === DEFAULT_SLOT) {
      return this.#readStringArray(profileShell()?.panel?.tabOrder);
    }
    return undefined;
  }

  getSlotOverride(viewId: string): string | undefined {
    const user = this.#userSlotOverrides[viewId];
    if (user !== undefined) return user;
    const profileDefaults = this.#readRecord(
      profileShell()?.viewDefaults?.slotOverrides
    );
    return profileDefaults?.[viewId];
  }

  // ── Setters (update cache immediately, then persist) ──

  async setDisplayMode(
    mode: TabDisplayMode,
    options?: SetOptions
  ): Promise<void> {
    await this.#persistScoped(KEY_DISPLAY, mode, options);
  }

  async setTabHidden(
    id: string,
    hidden: boolean,
    options?: SetOptions
  ): Promise<void> {
    const slot = options?.slot ?? DEFAULT_SLOT;
    const next = new Set(this.getHiddenTabs(slot));
    if (hidden) next.add(id);
    else next.delete(id);
    await this.#persistScoped(KEY_HIDDEN, Array.from(next), options);
  }

  async setTabOrder(ids: string[], options?: SetOptions): Promise<void> {
    await this.#persistScoped(KEY_ORDER, [...ids], options);
  }

  async setSlotOverride(
    viewId: string,
    slot: string,
    homeSlot: string,
    options?: SetOptions
  ): Promise<void> {
    const next = { ...this.#userSlotOverrides };
    if (slot === homeSlot) {
      // Reset-to-default: drop the override rather than storing an identity map.
      delete next[viewId];
    } else {
      next[viewId] = slot;
    }
    this.#userSlotOverrides = next;
    await this.#persist(KEY_SLOT_OVERRIDES, next, options);
  }

  // ── Internals ──

  /** Full settings key for a base + slot ('panel' → legacy unscoped key). */
  #keyFor(base: string, slot: string): string {
    return slot === DEFAULT_SLOT ? base : `${base}.${slot}`;
  }

  /** Read the user tier for a base+slot, memoised in the cache. */
  #userValue(base: string, slot: string): unknown {
    const key = this.#keyFor(base, slot);
    if (this.#cache.has(key)) return this.#cache.get(key);
    const value = this.#settings?.get?.('system', key);
    this.#cache.set(key, value);
    return value;
  }

  /** Update the cache for base+slot and persist under the scoped key. */
  async #persistScoped(
    base: string,
    value: unknown,
    options?: SetOptions
  ): Promise<void> {
    const slot = options?.slot ?? DEFAULT_SLOT;
    const key = this.#keyFor(base, slot);
    this.#cache.set(key, value);
    await this.#persist(key, value, options);
  }

  async #persist(
    key: string,
    value: unknown,
    options?: SetOptions
  ): Promise<void> {
    const settings = settingsManager();
    if (!settings) return;
    const scope = options?.workspace ? 'project' : 'global';
    try {
      await settings.set('system', key, value, scope);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[PanelLayout] failed to persist', key, err);
    }
  }

  #readMode(value: unknown): TabDisplayMode | undefined {
    return value === 'icon' || value === 'label' || value === 'both'
      ? value
      : undefined;
  }

  #readStringArray(value: unknown): string[] | undefined {
    return Array.isArray(value) && value.every((v) => typeof v === 'string')
      ? (value as string[])
      : undefined;
  }

  #readRecord(value: unknown): Record<string, string> | undefined {
    if (!value || typeof value !== 'object' || Array.isArray(value))
      return undefined;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (typeof v === 'string') out[k] = v;
    }
    return out;
  }
}

const panelLayout: shell.PanelLayout.IPanelLayoutManager =
  new PanelLayoutManager();

export { PanelLayoutManager };
export default panelLayout;
